/**
 * UserDeleteButton - Button for deleting a user.
 *
 * Displays a delete icon button that triggers the delete callback.
 * Manages its own loading state and shows a spinner while deleting.
 */

import { useState } from 'react';
import { Trash2, Loader2 } from 'lucide-react';
import { Button } from '@lean-agility/design-system/react';
import type { UserResponse } from '@/lib/api';

export interface UserDeleteButtonProps {
  /**
   * User to delete.
   */
  user: UserResponse;
  /**
   * Async callback when button is clicked.
   */
  onDelete: (user: UserResponse) => Promise<void>;
}

export function UserDeleteButton({ user, onDelete }: UserDeleteButtonProps) {
  const [isDeleting, setIsDeleting] = useState(false);

  const handleClick = async () => {
    setIsDeleting(true);
    try {
      await onDelete(user);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <Button variant="ghost" size="sm" onClick={handleClick} disabled={isDeleting} aria-label={`Delete ${user.email}`}>
      {isDeleting ? <Loader2 className="size-4 animate-spin" /> : <Trash2 className="size-4 text-feedback-error" />}
    </Button>
  );
}
